/**
 * The "Overrides" section of the sliding job launch drawer.
 * Let the user specify overrides (args, config, entry point) for
 * what we are launching.
 */

import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useEffect, useState} from 'react';

import {type JobDetails, type JobRun} from '../JobDetail/utilTypes';
import {isJobValidForPublicQueue} from '../publicQueue/configuration';
import {
  TEST_PUBLIC_QUEUE_NAME,
  WANDB_PUBLIC_QUEUE_NAME,
} from '../publicQueue/constants';
import {filterSchemasBySelectedTasks} from '../publicQueue/utils';
import {FILE_KEY_DELIMITER, type JsonObject, type Property} from '../utilTypes';
import {ArtifactPathSelector} from './Overrides/ArtifactPathSelector';
import {CheckboxEntry} from './Overrides/CheckboxEntry';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown} from './Overrides/Dropdown';
import {NestedOverrides} from './Overrides/NestedOverrides';
import {SecretDropdown} from './Overrides/SecretDropdown';
import {TextEntry} from './Overrides/TextEntry';
import {DOT, getNestedOverrideValue, getProperties} from './utils';

type JobDrawerStructuredOverridesProps = {
  overrides: JsonObject;
  fileOverrides?: JsonObject;
  onStructuredOverridesChange: (config: JsonObject, files?: JsonObject) => void;
  onSelectRun: (run: JobRun | null) => void;
  schemas: JsonObject;
  onSecretsStateChange?: (loading: boolean, error: boolean) => void;
  targetEntityName: string;
  openSecretsView: () => void;
  job: JobDetails;
};

function setOverrideValue(overrides: JsonObject, key: string, value: any) {
  let root: any = overrides;
  const remainingKeys = key.split(DOT);
  while (remainingKeys.length > 1) {
    const idx = remainingKeys.shift() as string;
    if (root[idx] == null) {
      root[idx] = {};
    }
    root = root[idx];
  }
  root[remainingKeys[0].replaceAll('\\.', '.')] = value;
}

const PropertyRenderer: React.FC<{
  property: Property;
  getOverrideValue: (property: Property) => any;
  onDropdownChange: (newValue: any) => void;
  onTextEntryChange: (event: any, context: any) => void;
  targetEntityName: string;
  openSecretsView: () => void;
}> = ({
  property,
  getOverrideValue,
  onDropdownChange,
  onTextEntryChange,
  targetEntityName,
  openSecretsView,
}) => {
  switch (property.type) {
    case 'string':
    case 'integer':
    case 'number':
      if (property.enum) {
        // string/number/int with enum: Dropdown
        return (
          <Dropdown
            property={property}
            value={getOverrideValue(property) as string | number | boolean}
            onChange={onDropdownChange}
          />
        );
      }

      // Handle different formats for primitive types without enum
      switch (property.format) {
        case 'secret':
          return (
            <SecretDropdown
              property={property}
              value={(getOverrideValue(property) as string) || null}
              onChange={onDropdownChange}
              targetEntityName={targetEntityName}
              openSecretsView={openSecretsView}
            />
          );

        case 'artifact_path':
          return (
            <ArtifactPathSelector
              property={property}
              value={getOverrideValue(property) as string}
              onChange={(value: string) => {
                onTextEntryChange(null, {
                  id: (property.file ?? '') + FILE_KEY_DELIMITER + property.key,
                  value,
                  type: 'string',
                });
              }}
            />
          );

        default:
          // string/number/int without enum: Freeform text entry
          return (
            <TextEntry
              property={property}
              value={(getOverrideValue(property) as string | number) ?? ''}
              onChange={onTextEntryChange}
            />
          );
      }

    case 'boolean':
      return (
        <CheckboxEntry
          property={property}
          value={getOverrideValue(property) as boolean}
          onChange={onDropdownChange}
        />
      );

    case 'array':
      if (property.items && Array.isArray(property.items.enum)) {
        // array of enum: Multi-select dropdown
        return (
          <Dropdown
            property={property}
            value={(getOverrideValue(property) as (string | number)[]) ?? []}
            onChange={onDropdownChange}
          />
        );
      }
      return null;

    case 'object':
      if (property.properties) {
        // object: Recursively group properties
        return (
          <NestedOverrides
            property={property}
            getElementForProperty={(prop: Property) => (
              <PropertyRenderer
                property={prop}
                getOverrideValue={getOverrideValue}
                onDropdownChange={onDropdownChange}
                onTextEntryChange={onTextEntryChange}
                targetEntityName={targetEntityName}
                openSecretsView={openSecretsView}
              />
            )}
          />
        );
      }
      return null;

    default:
      return null;
  }
};

export const JobDrawerStructuredOverrides = ({
  overrides,
  fileOverrides,
  onStructuredOverridesChange,
  onSelectRun,
  schemas,
  targetEntityName,
  openSecretsView,
  job,
}: JobDrawerStructuredOverridesProps) => {
  const [properties, setProperties] = useState<Property[]>([]);

  const onTextEntryChange = React.useCallback(
    (event: any, context: any) => {
      const [file, key] = context.id.split(FILE_KEY_DELIMITER);
      const newOverrides: JsonObject = overrides;
      const newFiles: JsonObject | undefined = fileOverrides;
      let newValue: any;
      if (context.type === 'number') {
        newValue = context.value === '' ? null : Number(context.value);
      } else {
        newValue = context.value;
      }

      if (file && newFiles && newFiles[file]) {
        setOverrideValue(newFiles[file] as JsonObject, key, newValue);
      } else {
        setOverrideValue(newOverrides, key, newValue);
      }
      onStructuredOverridesChange(newOverrides, newFiles);
      onSelectRun(null);
    },
    [overrides, fileOverrides, onStructuredOverridesChange, onSelectRun]
  );

  const onDropdownChange = React.useCallback(
    (newValue: any) => {
      const [file, key] = newValue.property.split(FILE_KEY_DELIMITER);
      const newOverrides: JsonObject = overrides;
      const newFiles: JsonObject | undefined = fileOverrides;
      if (file && newFiles && newFiles[file]) {
        setOverrideValue(newFiles[file] as JsonObject, key, newValue.value);
      } else {
        setOverrideValue(newOverrides, key, newValue.value);
      }
      onStructuredOverridesChange(newOverrides, newFiles);
      onSelectRun(null);
    },
    [overrides, fileOverrides, onStructuredOverridesChange, onSelectRun]
  );

  useEffect(() => {
    if (
      !isJobValidForPublicQueue(
        WANDB_PUBLIC_QUEUE_NAME,
        job.name,
        job.projectName,
        job.entityName
      ) &&
      !isJobValidForPublicQueue(
        TEST_PUBLIC_QUEUE_NAME,
        job.name,
        job.projectName,
        job.entityName
      )
    ) {
      setProperties(getProperties(schemas));
    } else {
      const selectedTasks = (overrides.tasks as string[]) ?? [];

      const filteredSchemas = filterSchemasBySelectedTasks(
        schemas,
        selectedTasks
      );
      setProperties(getProperties(filteredSchemas));
    }
  }, [schemas, overrides.tasks, job]);

  const getOverrideValue = (property: Property) => {
    if (property.file && fileOverrides && fileOverrides[property.file]) {
      return getNestedOverrideValue(
        fileOverrides[property.file] as JsonObject,
        property.key
      );
    }
    return getNestedOverrideValue(overrides, property.key);
  };

  return (
    <Tailwind>
      <div className="mt-10 w-full rounded border border-moon-250 bg-white p-12">
        {properties.map((property, idx, arr) => {
          return (
            <React.Fragment key={property.key}>
              <PropertyRenderer
                property={property}
                getOverrideValue={getOverrideValue}
                onDropdownChange={onDropdownChange}
                onTextEntryChange={onTextEntryChange}
                targetEntityName={targetEntityName}
                openSecretsView={openSecretsView}
              />
              {idx < arr.length - 1 && (
                <div className="m-[20px_-12px] h-px w-auto bg-moon-250" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </Tailwind>
  );
};
